import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { VideoCameraIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { Application, QuizCategory, QuizDifficulty, MockInterviewQuestion, MockInterviewSession, SubscriptionPlan } from '../types'
import { FREE_TIER_LIMITS, PRO_TIER_LIMITS } from '../types'
import {
  generateInterviewQuestions,
  fetchMockInterviewSessions,
  fetchMockInterviewCount,
  fetchWeeklyMockInterviewCount,
} from '../services/mockInterviewService'
import { UpgradePrompt } from './UpgradePrompt'
import { MockInterviewExecutor } from './MockInterviewExecutor'
import { formatDate } from '../utils/dateFormat'
import { PageLayout } from './PageLayout'
import { Skeleton } from './Skeleton'
import type { DateFormat } from '../types'

type Props = {
  userId: string
  plan: SubscriptionPlan
  applications: Application[]
  dateFormat: DateFormat
}

type InterviewMode = 'application' | 'general'

const DIFFICULTY_OPTIONS: { value: QuizDifficulty; label: string }[] = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
]

const COUNT_OPTIONS = [5, 8, 10]

const CATEGORY_OPTIONS: { value: QuizCategory; label: string; description: string }[] = [
  { value: 'technical', label: 'Technical', description: 'Coding & technology' },
  { value: 'conceptual', label: 'Conceptual', description: 'Architecture & design' },
  { value: 'behavioral', label: 'Behavioral', description: 'STAR method scenarios' },
]

export function MockInterviewPage({ userId, plan, applications, dateFormat }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preSelectedAppId = searchParams.get('app')

  // Interview state
  const [isInterviewActive, setIsInterviewActive] = useState(false)
  const [generatedQuestions, setGeneratedQuestions] = useState<MockInterviewQuestion[]>([])
  const [interviewContext, setInterviewContext] = useState<{
    positionTitle: string
    techStack: string[]
    applicationId: string | null
    generalRole: string | null
    generalTech: string[] | null
  } | null>(null)

  // Mode
  const [mode, setMode] = useState<InterviewMode>(preSelectedAppId ? 'application' : 'general')

  // Config state
  const [selectedAppId, setSelectedAppId] = useState<string>(preSelectedAppId || '')
  const [generalRole, setGeneralRole] = useState('')
  const [generalTechInput, setGeneralTechInput] = useState('')
  const [difficulty, setDifficulty] = useState<QuizDifficulty>('medium')
  const [questionCount, setQuestionCount] = useState(5)
  const [categories, setCategories] = useState<QuizCategory[]>(['technical', 'conceptual', 'behavioral'])

  // Loading/error
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // History & limits
  const [interviewHistory, setInterviewHistory] = useState<MockInterviewSession[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [weeklyCount, setWeeklyCount] = useState(0)

  const isPro = plan === 'pro'

  // Sorted applications for dropdown
  const sortedApps = useMemo(() =>
    [...applications]
      .filter(a => !a.archived)
      .sort((a, b) => a.company.localeCompare(b.company)),
    [applications]
  )

  const selectedApp = useMemo(() =>
    applications.find(a => a.id === selectedAppId) || null,
    [applications, selectedAppId]
  )

  // Limit checks
  const canStart = isPro
    ? weeklyCount < PRO_TIER_LIMITS.maxMockInterviewsPerWeek
    : totalCount < FREE_TIER_LIMITS.maxMockInterviews

  const limitMessage = isPro
    ? `${weeklyCount}/${PRO_TIER_LIMITS.maxMockInterviewsPerWeek} this week`
    : `${totalCount}/${FREE_TIER_LIMITS.maxMockInterviews} total`

  // Load history & counts
  useEffect(() => {
    if (!userId) return
    setIsLoadingHistory(true)

    Promise.all([
      fetchMockInterviewSessions(userId),
      fetchMockInterviewCount(userId),
      fetchWeeklyMockInterviewCount(userId),
    ])
      .then(([sessions, total, weekly]) => {
        setInterviewHistory(sessions)
        setTotalCount(total)
        setWeeklyCount(weekly)
      })
      .catch(err => console.error('Failed to load mock interview data:', err))
      .finally(() => setIsLoadingHistory(false))
  }, [userId])

  // Pre-select app from URL
  useEffect(() => {
    if (preSelectedAppId && sortedApps.some(a => a.id === preSelectedAppId)) {
      setSelectedAppId(preSelectedAppId)
      setMode('application')
    }
  }, [preSelectedAppId, sortedApps])

  const toggleCategory = (cat: QuizCategory) => {
    setCategories(prev => {
      if (prev.includes(cat)) {
        if (prev.length <= 1) return prev
        return prev.filter(c => c !== cat)
      }
      return [...prev, cat]
    })
  }

  const handleGenerate = async () => {
    if (mode === 'application' && !selectedApp) return
    if (mode === 'general' && !generalRole.trim()) return
    if (categories.length === 0) return

    setIsGenerating(true)
    setGenerateError(null)

    const positionTitle = mode === 'application'
      ? (selectedApp?.positionTitle || `${selectedApp?.company} position`)
      : generalRole.trim()

    const techStack = mode === 'application'
      ? (selectedApp?.techStack || [])
      : generalTechInput.split(',').map(s => s.trim()).filter(Boolean)

    try {
      const questions = await generateInterviewQuestions({
        applicationId: mode === 'application' ? selectedAppId : null,
        generalRole: mode === 'general' ? generalRole.trim() : undefined,
        generalTechStack: mode === 'general' ? techStack : undefined,
        difficulty,
        questionCount,
        categories,
      })
      setGeneratedQuestions(questions)
      setInterviewContext({
        positionTitle,
        techStack,
        applicationId: mode === 'application' ? selectedAppId : null,
        generalRole: mode === 'general' ? generalRole.trim() : null,
        generalTech: mode === 'general' ? techStack : null,
      })
      setIsInterviewActive(true)
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate questions')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleFinishInterview = (session: MockInterviewSession) => {
    setIsInterviewActive(false)
    setGeneratedQuestions([])
    setInterviewContext(null)
    setTotalCount(prev => prev + 1)
    setWeeklyCount(prev => prev + 1)
    navigate(`/interview/${session.id}`)
  }

  const handleCancelInterview = () => {
    setIsInterviewActive(false)
    setGeneratedQuestions([])
    setInterviewContext(null)
  }

  // If interview active, show executor
  if (isInterviewActive && generatedQuestions.length > 0 && interviewContext) {
    return (
      <div className="py-4">
        <MockInterviewExecutor
          userId={userId}
          questions={generatedQuestions}
          positionTitle={interviewContext.positionTitle}
          techStack={interviewContext.techStack}
          difficulty={difficulty}
          categories={categories}
          applicationId={interviewContext.applicationId}
          generalRole={interviewContext.generalRole}
          generalTech={interviewContext.generalTech}
          onFinish={handleFinishInterview}
          onCancel={handleCancelInterview}
        />
      </div>
    )
  }

  const scoreColor = (score: number) =>
    score >= 80 ? 'text-emerald-500' :
    score >= 60 ? 'text-amber-500' :
    'text-rose-500'

  const isStartDisabled = mode === 'application'
    ? !selectedAppId || categories.length === 0 || isGenerating
    : !generalRole.trim() || categories.length === 0 || isGenerating

  return (
    <PageLayout
      icon={VideoCameraIcon}
      title="Mock Interview"
      subtitle="AI-powered speech-to-speech interview practice"
    >
      {/* Limit reached - Free users */}
      {!canStart && !isPro && (
        <div className="card p-6">
          <UpgradePrompt type="feature" featureName="Mock Interviews" />
        </div>
      )}

      {/* Limit reached - Pro users */}
      {!canStart && isPro && (
        <div className="card p-6">
          <div className="text-center space-y-3">
            <p className="text-lg font-semibold text-app-ink">Weekly limit reached</p>
            <p className="text-sm text-app-muted">
              You&apos;ve used {PRO_TIER_LIMITS.maxMockInterviewsPerWeek} of {PRO_TIER_LIMITS.maxMockInterviewsPerWeek} mock interviews this week.
              Your limit resets on a rolling 7-day basis.
            </p>
          </div>
        </div>
      )}

      {/* Config */}
      {canStart && (
        <div className="card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-app-ink font-display">New Interview</h2>
            <span className="text-xs text-app-muted bg-app-page rounded-full px-3 py-1">
              {limitMessage}
            </span>
          </div>

          {/* Mode selector */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">
              Interview Mode
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setMode('application')}
                className={`flex-1 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all ${
                  mode === 'application'
                    ? 'border-app-accent bg-app-highlight text-app-accent'
                    : 'border-app-line text-app-muted hover:border-app-line'
                }`}
              >
                <div className="font-semibold">For a job application</div>
                <div className="text-xs mt-1 opacity-75">Questions tailored to a specific role</div>
              </button>
              <button
                onClick={() => setMode('general')}
                className={`flex-1 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all ${
                  mode === 'general'
                    ? 'border-app-accent bg-app-highlight text-app-accent'
                    : 'border-app-line text-app-muted hover:border-app-line'
                }`}
              >
                <div className="font-semibold">General practice</div>
                <div className="text-xs mt-1 opacity-75">Enter any role &amp; tech stack</div>
              </button>
            </div>
          </div>

          {/* Application mode: dropdown */}
          {mode === 'application' && (
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">
                Application
              </label>
              <select
                value={selectedAppId}
                onChange={(e) => setSelectedAppId(e.target.value)}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                <option value="">Select an application...</option>
                {sortedApps.map(app => (
                  <option key={app.id} value={app.id}>
                    {app.company}{app.positionTitle ? ` — ${app.positionTitle}` : ''}
                  </option>
                ))}
              </select>
              {selectedApp && !selectedApp.rawMarkdown?.trim() && (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                  ⚠️ No job description available — questions will be based on limited context
                </p>
              )}
            </div>
          )}

          {/* General mode: role + tech */}
          {mode === 'general' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-app-ink mb-1.5">
                  Job Title / Role
                </label>
                <input
                  type="text"
                  value={generalRole}
                  onChange={(e) => setGeneralRole(e.target.value)}
                  placeholder="e.g., Senior Frontend Developer"
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-app-ink mb-1.5">
                  Tech Stack <span className="text-app-muted font-normal">(optional, comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={generalTechInput}
                  onChange={(e) => setGeneralTechInput(e.target.value)}
                  placeholder="e.g., React, TypeScript, Node.js"
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Difficulty + Count */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">
                Difficulty
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as QuizDifficulty)}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {DIFFICULTY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-app-ink mb-1.5">
                Number of Questions
              </label>
              <select
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {COUNT_OPTIONS.map(n => (
                  <option key={n} value={n}>{n} questions</option>
                ))}
              </select>
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">
              Categories
            </label>
            <div className="flex flex-wrap gap-3">
              {CATEGORY_OPTIONS.map(cat => {
                const isChecked = categories.includes(cat.value)
                return (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-2.5 rounded-lg border-2 px-4 py-2.5 cursor-pointer transition-all focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-app-accent ${
                      isChecked
                        ? 'border-app-accent bg-app-highlight text-app-accent'
                        : 'border-app-line text-app-muted hover:border-app-line'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleCategory(cat.value)}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{cat.label}</span>
                    <span className="text-xs opacity-60">{cat.description}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Error */}
          {generateError && (
            <div className="rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
              {generateError}
            </div>
          )}

          {/* Start button */}
          <button
            onClick={handleGenerate}
            disabled={isStartDisabled}
            className="flex items-center justify-center gap-2 w-full rounded-lg bg-app-primary px-5 py-3 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGenerating ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Generating interview questions...
              </>
            ) : (
              <>
                <VideoCameraIcon className="h-5 w-5" />
                Start Mock Interview
              </>
            )}
          </button>

          {/* Speech info */}
          <p className="text-xs text-app-muted text-center">
            The AI interviewer will speak questions aloud. You can answer by voice or text.
            Sessions are capped at 30 minutes.
          </p>
        </div>
      )}

      {/* History */}
      {isLoadingHistory ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-40" />
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-4 flex items-center gap-4">
              <Skeleton className="h-10 w-16 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      ) : interviewHistory.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-app-ink font-display">History</h2>
          {interviewHistory.slice(0, 15).map(session => {
            const label = session.application_id
              ? `Interview Session`
              : `${session.general_role || 'General'} Practice`

            return (
              <Link
                key={session.id}
                to={`/interview/${session.id}`}
                className="card p-4 flex items-center gap-4 hover:bg-app-subtle transition-colors group"
              >
                {/* Score circle */}
                <div className={`text-2xl font-bold w-14 text-center ${scoreColor(session.score)}`}>
                  {session.score}%
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-ink truncate">
                    {label}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs text-app-muted capitalize">
                      {session.difficulty}
                    </span>
                    <span className="text-xs text-app-muted">·</span>
                    <span className="text-xs text-app-muted">
                      {session.total_questions} questions
                    </span>
                    {session.duration_seconds && (
                      <>
                        <span className="text-xs text-app-muted">·</span>
                        <span className="text-xs text-app-muted">
                          {Math.round(session.duration_seconds / 60)} min
                        </span>
                      </>
                    )}
                    <span className="text-xs text-app-muted">·</span>
                    <span className="text-xs text-app-muted">
                      {formatDate(session.created_at, dateFormat)}
                    </span>
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRightIcon className="h-5 w-5 text-app-muted group-hover:text-app-muted transition-colors" />
              </Link>
            )
          })}
        </div>
      ) : null}
    </PageLayout>
  )
}
